import * as types from '../mutation-types';
import ContactAPI from '../../api/contacts';
import ConversationApi from '../../api/conversations';
import camelcaseKeys from 'camelcase-keys';
import { ExceptionWithMessage } from 'shared/helpers/CustomErrors';

export const createMessagePayload = (payload, message) => {
  const { content, cc_emails, bcc_emails, private: isPrivate, template_params } =
    message;
  payload.append('message[content]', content ?? '');
  if (cc_emails) payload.append('message[cc_emails]', cc_emails);
  if (bcc_emails) payload.append('message[bcc_emails]', bcc_emails);
  if (typeof isPrivate === 'boolean') {
    payload.append('message[private]', isPrivate);
  }
  if (template_params) {
    payload.append(
      'message[template_params]',
      typeof template_params === 'string'
        ? template_params
        : JSON.stringify(template_params)
    );
  }
};

export const createConversationPayload = ({ params, contactId, files }) => {
  const { inboxId, message, sourceId, mailSubject, assigneeId } = params;
  const payload = new FormData();

  if (message) {
    createMessagePayload(payload, message);
  }

  if (files && files.length > 0) {
    files.forEach(file => payload.append('message[attachments][]', file));
  }

  payload.append('inbox_id', inboxId);
  payload.append('contact_id', contactId);
  payload.append('source_id', sourceId);
  payload.append('additional_attributes[mail_subject]', mailSubject);
  payload.append('assignee_id', assigneeId);

  return payload;
};

export const createWhatsAppConversationPayload = ({ params, files }) => {
  const { inboxId, message, contactId, sourceId, assigneeId } = params;
  const attachmentFiles = files?.length ? files : params.files;

  if (attachmentFiles?.length) {
    const payload = new FormData();
    payload.append('inbox_id', inboxId);
    payload.append('contact_id', contactId);
    payload.append('source_id', sourceId);
    payload.append('assignee_id', assigneeId);
    createMessagePayload(payload, message);
    attachmentFiles.forEach(file =>
      payload.append('message[attachments][]', file)
    );
    return payload;
  }

  return {
    inbox_id: inboxId,
    contact_id: contactId,
    source_id: sourceId,
    message,
    assignee_id: assigneeId,
  };
};

const setNewConversationPayload = ({
  isFromWhatsApp,
  params,
  contactId,
  files,
}) => {
  if (isFromWhatsApp) {
    return createWhatsAppConversationPayload({
      params,
      files: files?.length ? files : params.files,
    });
  }
  return createConversationPayload({
    params,
    contactId,
    files,
  });
};

const state = {
  records: {},
  meta: {},
  uiFlags: {
    isFetching: false,
    isFetchingList: false,
  },
};

export const getters = {
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getContactConversation: $state => id => {
    return $state.records[Number(id)] || [];
  },
  getAllConversationsByContactId: $state => id => {
    const records = $state.records[Number(id)] || [];
    return camelcaseKeys(records, { deep: true });
  },
  getHasMoreConversations: $state => id => {
    const meta = $state.meta[Number(id)];
    if (!meta) return false;
    const loaded = ($state.records[Number(id)] || []).length;
    return loaded < meta.count;
  },
};

export const actions = {
  create: async ({ commit }, { params, isFromWhatsApp }) => {
    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isCreating: true,
    });
    const { contactId, files } = params;
    try {
      const payload = setNewConversationPayload({
        isFromWhatsApp,
        params,
        contactId,
        files,
      });

      const { data } = await ConversationApi.create(payload);
      commit(types.default.ADD_CONTACT_CONVERSATION, {
        id: contactId,
        data,
      });

      return data;
    } catch (error) {
      const message =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message;
      throw new ExceptionWithMessage(message);
    } finally {
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isCreating: false,
      });
    }
  },
  get: async ({ commit }, contactId) => {
    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isFetching: true,
    });
    try {
      const response = await ContactAPI.getConversations(contactId, 1);
      const { payload, meta } = response.data;
      commit(types.default.SET_CONTACT_CONVERSATIONS, {
        id: contactId,
        data: payload,
      });
      commit(types.default.SET_CONTACT_CONVERSATIONS_META, {
        id: contactId,
        meta,
      });
    } catch (error) {
      // Swallow fetch errors; the UI falls back to an empty history list.
    } finally {
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isFetching: false,
      });
    }
  },
  fetchPrevious: async (
    { commit, state: $state, getters: $getters },
    contactId
  ) => {
    if (
      !$getters.getHasMoreConversations(contactId) ||
      $state.uiFlags.isFetchingList
    ) {
      return;
    }
    commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
      isFetchingList: true,
    });
    try {
      const currentPage = $state.meta[Number(contactId)]?.current_page || 1;
      const nextPage = currentPage + 1;
      const response = await ContactAPI.getConversations(contactId, nextPage);
      const { payload, meta } = response.data;
      commit(types.default.APPEND_CONTACT_CONVERSATIONS, {
        id: contactId,
        data: payload,
      });
      commit(types.default.SET_CONTACT_CONVERSATIONS_META, {
        id: contactId,
        meta,
      });
    } catch (error) {
      // Swallow fetch errors; the next scroll attempt can retry.
    } finally {
      commit(types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG, {
        isFetchingList: false,
      });
    }
  },
};

export const mutations = {
  [types.default.SET_CONTACT_CONVERSATIONS_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },
  [types.default.SET_CONTACT_CONVERSATIONS]: ($state, { id, data }) => {
    $state.records = {
      ...$state.records,
      [Number(id)]: data,
    };
  },
  [types.default.APPEND_CONTACT_CONVERSATIONS]: ($state, { id, data }) => {
    const existing = $state.records[Number(id)] || [];
    const existingIds = new Set(existing.map(conversation => conversation.id));
    const merged = [
      ...existing,
      ...data.filter(conversation => !existingIds.has(conversation.id)),
    ];
    $state.records = {
      ...$state.records,
      [Number(id)]: merged,
    };
  },
  [types.default.SET_CONTACT_CONVERSATIONS_META]: ($state, { id, meta }) => {
    $state.meta = {
      ...$state.meta,
      [Number(id)]: meta,
    };
  },
  [types.default.ADD_CONTACT_CONVERSATION]: ($state, { id, data }) => {
    const conversations = $state.records[id] || [];

    const updatedConversations = [...conversations];
    const index = conversations.findIndex(
      conversation => conversation.id === data.id
    );

    if (index !== -1) {
      updatedConversations[index] = { ...conversations[index], ...data };
    } else {
      updatedConversations.push(data);
    }

    $state.records = {
      ...$state.records,
      [id]: updatedConversations,
    };
  },
  [types.default.DELETE_CONTACT_CONVERSATION]: ($state, id) => {
    const { [id]: deletedRecord, ...remainingRecords } = $state.records;
    $state.records = remainingRecords;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
